/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { useMemo } from 'react';
import NumberFormat from '../../../utils/number-format';
import Dashboard, { DashboardColumn, DashboardRow, DashboardSection } from '../../dashboard';
import Loader from '../../loader';
import Table, { TableColumn } from '../../table/Table';
import Error from '../../error';
import { formatDate, DATE } from '../../../utils/date';
import SettingsSidebar, { SearchBox, SettingsSection } from '../../settings-sidebar';
import { escapeRegexCharacters } from '../../../utils/string-format';
import { convertToDateParams, getDisplayCompleted, getDisplaySurveyName } from '../utils';

import { generateUrl } from '../../../routes/util';
import { ROUTES } from '../../../constants/routes';
import { useHistory } from 'react-router-dom';
import { ReportTypes } from '../../../types/statsTypes';

import { useReportData } from '../hooks/useReportData';
import { convertBinaryToOctetStream, getWorkbook } from '../../../utils/file/XlsxFile';
import FileSaver from 'file-saver';
import { StaffFilters } from '../partials/StaffFilters';
import { useStaffFilters } from '../hooks/useStaffFilters';
import { QUESTION } from '../../../constants/terminology';

const SGXTag = 'sgx_metrics';
const tagKey = 'sgx_metrics_verified';

interface StatsData {
  _id: string,
  name: string,
  scopeCount: number,
  answeredCount: number,
  verifiedCount: number,
  rejectedCount: number,
  [tagKey]: number,
  created: string;
  effectiveDate: string;
  initiativeName: string;
  initiativeId: string,
  completedDate?: string,
}

interface StatsRow extends StatsData {
  surveyName: string;
  onClick: () => void;
}

const columns: TableColumn<StatsRow>[] = [
  {
    Header: 'Company name',
    accessor: 'initiativeName',
    Cell: (a) => a.row.original.initiativeName,
  },
  {
    Header: 'Survey name',
    accessor: 'surveyName',
    Cell: (a) => getDisplaySurveyName(a.row.original.surveyName, a.row.original.onClick),
  },
  {
    Header: 'Survey created',
    accessor: 'created',
    Cell: (a) => formatDate(a.row.original.created, DATE.DEFAULT_SPACES),
  },
  {
    Header: 'Qs In Scope',
    accessor: 'scopeCount',
    cellProps: {
      className: 'text-center',
    },
    Cell: (a) => <NumberFormat value={a.row.original.scopeCount} />,
  },
  {
    Header: 'Answered',
    accessor: 'answeredCount',
    cellProps: {
      className: 'text-center',
    },
    Cell: (a) => <NumberFormat value={a.row.original.answeredCount} />,
  },
  {
    Header: 'Verified',
    accessor: 'verifiedCount',
    cellProps: {
      className: 'text-center',
    },
    Cell: (a) => <NumberFormat value={a.row.original.verifiedCount} />,
  },
  {
    Header: 'Rejected',
    accessor: 'rejectedCount',
    cellProps: {
      className: 'text-center',
    },
    Cell: (a) => <NumberFormat value={a.row.original.rejectedCount} />,
  },
  {
    Header: 'Completed',
    accessor: 'completedDate',
    cellProps: {
      className: 'text-center',
    },
    Cell: (a) => getDisplayCompleted(a.row.original.completedDate),
  },
  {
    Header: 'Completed Date',
    accessor: (row) => (row.completedDate ? <span>{formatDate(row.completedDate, DATE.DEFAULT_SPACES)}</span> : null),
  },
  {
    Header: 'Verified SGX Core Qs',
    accessor: tagKey,
    cellProps: {
      className: 'text-center',
    },
    Cell: (a) => <NumberFormat value={a.row.original[tagKey]} />,
  },
];

const Survey = () => {
  const history = useHistory();
  const [searchText, setSearchText] = React.useState('');
  const [hasError, setHasError] = React.useState(false);
  const [rows, setRows] = React.useState<StatsData[] | undefined>();

  const staffFilterProps = useStaffFilters();
  const { extraFilterApiParams, ...staffFilters } = staffFilterProps;

  const extraProps = useMemo(
    () => ({
      scopeTag: SGXTag,
      ...extraFilterApiParams,
    }),
    [extraFilterApiParams]
  );

  const successCallback = React.useCallback((data?: StatsData[]) => {
    setHasError(false);
    setRows(data);
  }, []);

  const errorCallback = React.useCallback(() => {
    setHasError(true)
  }, []);

  useReportData({
    reportType: ReportTypes.Survey,
    successCallback,
    errorCallback,
    extraProps,
    dateParams: convertToDateParams(staffFilterProps.dateRange),
  });

  const handleDownloadStatistic = () => {
    if (!rows || rows.length === 0) {
      return;
    }
    // Headers must be strings
    const rowHeaders = columns.map((col) => col.Header as string);
    const rowValues = filteredRows.map((r) => [
      r.initiativeName,
      r.surveyName,
      formatDate(r.created, DATE.DEFAULT_SPACES),
      r.scopeCount,
      r.answeredCount,
      r.verifiedCount,
      r.rejectedCount,
      getDisplayCompleted(r.completedDate),
      r.completedDate ? formatDate(r.completedDate, DATE.DEFAULT_SPACES) : '',
      r[tagKey],
    ]);
    const data = [[rowHeaders, ...rowValues]];
    const fileName = `survey-statistics_${formatDate(new Date(), DATE.FILE_NAME)}`;

    const workBook = getWorkbook(data, {
      title: fileName,
      subject: fileName,
      author: 'g17eco',
      createDate: new Date(),
      sheetNames: ['Survey Statistics'],
    });
    const blob = new Blob([convertBinaryToOctetStream(workBook)], { type: 'application/octet-stream' });
    FileSaver.saveAs(blob, `${fileName}.xlsx`);
  };

  const filteredRows = React.useMemo(() => {
    if (!rows) {
      return [];
    }

    const filterScore = (row: StatsRow) => {
      if (searchText) {
        const escapedSearchText = escapeRegexCharacters(searchText.trim().toLowerCase());
        const searchTextRegex = new RegExp(escapedSearchText.replace(/ /g, '|'), 'g');
        const searchString = `${row.surveyName}`.toLowerCase();
        return (searchString.match(searchTextRegex) || []).length;
      }
      return 1;
    }

    return rows
      .map(r => {
        return {
          ...r,
          surveyName: r.name || `${r.initiativeName} ${formatDate(r.effectiveDate, DATE.MONTH_YEAR, true)}`,
          onClick: () => history.push(
            generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, { initiativeId: r.initiativeId, surveyId: r._id, page: 'overview' })
          )
        }
      })
      .filter(filterScore);
  }, [rows, searchText, history]);

  if (hasError) {
    return <Error />;
  }

  const total = {
    count: filteredRows.length,
    scopeCount: filteredRows.reduce((acc, d) => acc + d.scopeCount, 0),
    answeredCount: filteredRows.reduce((acc, d) => acc + d.answeredCount, 0),
    verifiedCount: filteredRows.reduce((acc, d) => acc + d.verifiedCount, 0),
    completedCount: filteredRows.reduce((acc, d) => acc + (d.completedDate ? 1 : 0), 0)
  };

  const isLoading = rows === undefined;

  return (
    <Dashboard hasSidebar>
      <SettingsSidebar>
        <SettingsSection
          title={'Search Surveys'}
          subtitle='Search for specific surveys by name.'
          icon='fa-search'
          buttons={[
            {
              icon: 'fa-times-circle',
              tooltip: 'Clear search',
              onClick: () => setSearchText(''),
            }
          ]}>
          <SearchBox
            handleOnChange={(e) => setSearchText(String((e.target as HTMLInputElement).value))}
            value={searchText}
          />
        </SettingsSection>
        <StaffFilters {...staffFilters} reportType={ReportTypes.Survey} />
      </SettingsSidebar>
      <DashboardRow>
        <DashboardColumn
          title='Number of Surveys'
          flexBasisPc={'20%'}
        >
          <div className='text-right'>
            <NumberFormat value={total.count} />
          </div>
        </DashboardColumn>
        <DashboardColumn
          title='Completed Surveys'
          flexBasisPc={'20%'}
        >
          <div className='text-right'>
            <NumberFormat value={100 * total.completedCount / total.count} decimalPlaces={1} suffix={'%'} />
          </div>
        </DashboardColumn>
        <DashboardColumn
          title={`Total ${QUESTION.CAPITALIZED_PLURAL}`}
          flexBasisPc={'20%'}
        >
          <div className='text-right'>
            <NumberFormat value={total.scopeCount} />
          </div>
        </DashboardColumn>
        <DashboardColumn
          title={`Answered ${QUESTION.CAPITALIZED_PLURAL}`}
          flexBasisPc={'20%'}
        >
          <div className='text-right'>
            <NumberFormat value={100 * total.answeredCount / total.scopeCount} decimalPlaces={1} suffix={'%'} />
          </div>
        </DashboardColumn>
        <DashboardColumn
          title={`Verified ${QUESTION.CAPITALIZED_PLURAL}`}
          flexBasisPc={'20%'}
        >
          <div className='text-right'>
            <NumberFormat value={100 * total.verifiedCount / total.scopeCount} decimalPlaces={1} suffix={'%'} />
          </div>
        </DashboardColumn>
      </DashboardRow>
      <DashboardSection
        title={<span>Survey Statistics <i className='fas fa-file-excel cursor-pointer ml-2' onClick={handleDownloadStatistic}></i></span>}
        icon='fa-clipboard-list'
      >
        {isLoading
          ?
          <Loader />
          :
          filteredRows.length === 0
            ?
            <>No results</>
            :
            <Table<StatsRow>
              columns={columns}
              data={filteredRows}
            />
        }
      </DashboardSection>
    </Dashboard>
  );
}

export default Survey;
